import React from 'react';

import { Show } from '@cowbell/ui';
import { Box, Popover, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { useParams } from 'react-router-dom';

import { useNotifications } from '../../../../../_lib/notifications/useNotifications';
import type {
  ExtractionResultItem,
  RagSourceReference,
} from '../../../../../api/document-rag/document-rag.api.types';
import type { FieldType } from '../../../../../form-engine/parsing/parser';
import type { FormFieldDef } from '../../../../../form-engine/types';
import { useDocumentRag } from '../../_hooks/useDocumentRag';
import { useSubmissionConfidence } from '../../_hooks/useSubmissionConfidence';
import { useUpdateConfidenceScores } from '../../_hooks/useUpdateConfidenceScores';
import { VERIFIED_CONFIDENCE_SCORE } from '../../_state/submission-confidence.state';
import {
  transformRagValueForFieldType,
  requiresTransformation,
} from '../../_utils/rag-value-transformers';
import { DocumentRagIcon } from '../../components/DocumentRagIcon';
import { ExtractionCardLoadingContent } from './ExtractionCardLoadingContent';
import { ExtractionCardResultsHeaderActions } from './ExtractionCardResultsHeaderActions';
import { ExtractionCardShell } from './ExtractionCardShell';
import { ExtractionResultsContent } from './ExtractionResultsContent';

/**
 * Props for FieldExtractionPopover component
 */
export interface FieldExtractionPopoverProps {
  /** Whether the extraction trigger is disabled */
  disabled?: boolean;
  /** Whether an extraction request is currently loading */
  loading?: boolean;
  /** Whether the extraction request encountered an error */
  isError?: boolean;
  /** The extraction result, or null if no extraction has been performed or completed - can be single or array for multiple values */
  extraction: ExtractionResultItem | ExtractionResultItem[] | null;
  /** Function to trigger a new extraction request. Pass { force: true } to bypass cache. */
  triggerExtraction: (options?: { force?: boolean }) => void;
  /** Optional: Whether to show the component (used by parent components for conditional rendering) */
  show?: boolean;
  /** Optional: Data test ID for testing purposes */
  'data-testid'?: string;
  /** Optional: Form field name to update when swap button is clicked */
  fieldName?: string;
  /** Optional: Field label to display in the popover */
  fieldLabel?: string;
  /** Optional: Form submit handler to call after swapping value - receives form data and should return a Promise */
  onSubmit?: <T = Record<string, unknown>>(data: T) => Promise<unknown> | void;
  /** Optional: Custom swap handler - if provided, bypasses form context and handles save directly */
  onSwapValues?: (
    values: ExtractionResultItem | ExtractionResultItem[]
  ) => Promise<void> | void;
  /** Optional: Whether the target field supports multi-select (e.g., otherDomains, additionalNamedInsured) */
  isMultiSelect?: boolean;
  /** Optional: Current field value for pre-selection in multi-select mode (used when not in FormProvider) */
  currentFieldValue?: string | string[] | null;
  /** Optional: Field type for complex field transformations (dynamic form fields) */
  fieldType?: FieldType;
  /** Optional: Field metadata for complex field transformations (dynamic form fields) */
  fieldMeta?: FormFieldDef;
}

/**
 * Inner component that handles swap functionality with form context
 * This component must be rendered within a FormProvider when fieldName is provided
 */
function FieldExtractionPopoverWithForm({
  extraction,
  fieldName,
  fieldLabel,
  onSubmit,
  onSwapValuesCustom,
  onCitationClick,
  isMultiSelect = false,
  currentFieldValueProp = null,
  fieldType,
  fieldMeta,
}: {
  extraction: ExtractionResultItem | ExtractionResultItem[] | null;
  fieldName: string;
  fieldLabel?: string;
  onSubmit?: <T = Record<string, unknown>>(data: T) => Promise<unknown> | void;
  onSwapValuesCustom?: (
    values: ExtractionResultItem | ExtractionResultItem[]
  ) => Promise<void> | void;
  onCitationClick?: (citation: RagSourceReference) => void;
  isMultiSelect?: boolean;
  currentFieldValueProp?: string | string[] | null;
  fieldType?: FieldType;
  fieldMeta?: FormFieldDef;
}) {
  const formContext = useFormContext();
  const notifications = useNotifications();
  const params = useParams<{ submissionId?: string }>();
  const { updateScores } = useUpdateConfidenceScores();
  const { locked } = useSubmissionConfidence();

  // Get current field value to pre-select matching extraction values in multi-select mode
  // Use form context if available, otherwise use the prop value (for components without FormProvider)
  const formContextValue = formContext?.watch(fieldName) ?? null;
  const currentFieldValue = formContextValue ?? currentFieldValueProp;

  const handleSwapValues = React.useCallback(
    async (values: ExtractionResultItem | ExtractionResultItem[]) => {
      // If a custom swap handler is provided, use it instead of form context logic
      if (onSwapValuesCustom) {
        try {
          await onSwapValuesCustom(values);
        } catch (error) {
          console.error('Custom swap handler failed:', error);
          notifications.error('Failed to swap values');
        }
        return;
      }

      if (!fieldName) {
        notifications.error('Field name not specified');
        return;
      }

      if (!formContext) {
        notifications.error('Form context not available');
        return;
      }

      // Handle multi-select: array of ExtractionResultItems
      if (Array.isArray(values)) {
        if (values.length === 0) {
          notifications.error('No values selected to swap');
          return;
        }

        // Extract raw values from all selected items
        const rawValues = values
          .map((item) => item.raw_value ?? item.formatted_value)
          .filter((val) => val !== null && val !== undefined)
          .map((val) => String(val));

        if (rawValues.length === 0) {
          notifications.error('No valid values to swap');
          return;
        }

        try {
          // Set the field with the array of values
          formContext.setValue(fieldName, rawValues, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });

          // Trigger validation
          await formContext.trigger(fieldName);

          // Update confidence scores
          if (params.submissionId) {
            try {
              await updateScores(params.submissionId, {
                [fieldName]: VERIFIED_CONFIDENCE_SCORE,
              });
            } catch (confidenceError) {
              console.warn(
                'Failed to update confidence score on swap:',
                confidenceError
              );
            }
          }

          // Submit the form if onSubmit handler is provided
          if (onSubmit) {
            // Get current form values and merge with swapped field
            // We call onSubmit directly instead of handleSubmit because
            // React's state batching means dirtyFields hasn't updated yet
            const currentFormData = formContext.getValues();
            const dataWithSwappedField = {
              ...currentFormData,
              [fieldName]: rawValues,
              // Pass swapped field key so onSubmit can bypass dirtyFields check
              _ragSwappedFields: [fieldName],
            };

            try {
              await onSubmit(dataWithSwappedField);
            } catch (submitError) {
              console.error('Submit failed:', submitError);
              notifications.error('Failed to save swapped values');
            }
          } else {
            notifications.success(
              `${rawValues.length} value${
                rawValues.length > 1 ? 's' : ''
              } swapped to form field`
            );
          }
        } catch (error) {
          console.error('Failed to swap values:', error);
          notifications.error('Failed to swap values');
        }
        return;
      }

      // Handle single select: single ExtractionResultItem
      const value = values;
      const rawValue = value.raw_value;

      // Check if this is a complex field type that requires transformation
      if (fieldType && requiresTransformation(fieldType)) {
        const transformedValue = transformRagValueForFieldType(
          rawValue,
          fieldType,
          fieldMeta
        );

        try {
          formContext.setValue(fieldName, transformedValue, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });

          await formContext.trigger(fieldName);

          // Update confidence scores
          if (params.submissionId) {
            try {
              await updateScores(params.submissionId, {
                [fieldName]: VERIFIED_CONFIDENCE_SCORE,
              });
            } catch (confidenceError) {
              console.warn(
                'Failed to update confidence score on swap:',
                confidenceError
              );
            }
          }

          // Submit the form if onSubmit handler is provided
          if (onSubmit) {
            const currentFormData = formContext.getValues();
            const dataWithSwappedField = {
              ...currentFormData,
              [fieldName]: transformedValue,
              _ragSwappedFields: [fieldName],
            };

            try {
              await onSubmit(dataWithSwappedField);
            } catch (submitError) {
              console.error('Submit failed:', submitError);
              notifications.error('Failed to save swapped value');
            }
          } else {
            notifications.success('Value swapped to form field');
          }
        } catch (error) {
          console.error('Failed to swap complex field value:', error);
          notifications.error('Failed to swap value');
        }
        return;
      }

      // Standard handling for simple field types
      const isArrayValue = Array.isArray(rawValue);
      const isObjectValue =
        rawValue !== null && typeof rawValue === 'object' && !isArrayValue;

      // For objects/arrays, only use raw_value (never fall back to formatted_value)
      // For primitives, allow formatted_value as fallback
      const valueToSet =
        isObjectValue || isArrayValue
          ? rawValue
          : rawValue ?? value.formatted_value ?? null;

      if (valueToSet === null || valueToSet === undefined) {
        notifications.error('No value to swap');
        return;
      }

      try {
        const fieldsToUpdate: Record<string, unknown> = {};
        const fieldNamesToTrigger: string[] = [];

        if (isObjectValue) {
          // Check if fieldName refers to a nested container field in the form
          // e.g., mailingAddress has fields like mailingAddress.address1, mailingAddress.city
          // while physicalAddress uses flat fields like address1, city
          const containerValue = formContext.getValues(fieldName);
          const isNestedContainerField =
            containerValue !== undefined &&
            typeof containerValue === 'object' &&
            containerValue !== null;

          // Object: iterate over keys and set each field
          for (const [key, fieldValue] of Object.entries(
            valueToSet as unknown as Record<string, unknown>
          )) {
            const formValue =
              fieldValue !== null && fieldValue !== undefined
                ? String(fieldValue)
                : '';

            // Use nested path for container fields (e.g., mailingAddress.address1)
            // Use flat path for non-container fields (e.g., address1 for physicalAddress)
            const actualFieldName = isNestedContainerField
              ? `${fieldName}.${key}`
              : key;

            formContext.setValue(actualFieldName, formValue, {
              shouldDirty: true,
              shouldValidate: true,
              shouldTouch: true,
            });
            fieldsToUpdate[actualFieldName] = formValue;
            fieldNamesToTrigger.push(actualFieldName);
          }
        } else if (isArrayValue) {
          // Array: set the field directly with the array value
          formContext.setValue(fieldName, valueToSet, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });
          fieldsToUpdate[fieldName] = valueToSet;
          fieldNamesToTrigger.push(fieldName);
        } else {
          // Primitive: set single field.
          // Many form fields (Radio, SingleSelect, Checkbox, Currency, etc.) store values
          // as objects like { value: "Yes", comment?: "" } rather than plain strings.
          // If the current form value already has that shape, preserve it so Yup
          // doesn't cast the incoming string to null and fail object-type validation.
          const currentValBeforeSet = formContext.getValues(fieldName);
          const isValueObjectShape =
            currentValBeforeSet !== null &&
            currentValBeforeSet !== undefined &&
            typeof currentValBeforeSet === 'object' &&
            !Array.isArray(currentValBeforeSet) &&
            'value' in currentValBeforeSet;

          const primitiveValue = isValueObjectShape
            ? { ...currentValBeforeSet, value: String(valueToSet) }
            : String(valueToSet);

          formContext.setValue(fieldName, primitiveValue, {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true,
          });

          fieldsToUpdate[fieldName] = primitiveValue;
          fieldNamesToTrigger.push(fieldName);
        }

        // Trigger validation for all updated fields
        await Promise.all(
          fieldNamesToTrigger.map((field) => formContext.trigger(field))
        );

        // Update confidence scores to 100% for all swapped fields
        if (params.submissionId) {
          try {
            const confidenceScores = fieldNamesToTrigger.reduce(
              (acc, field) => ({ ...acc, [field]: VERIFIED_CONFIDENCE_SCORE }),
              {}
            );
            await updateScores(params.submissionId, confidenceScores);
          } catch (confidenceError) {
            console.warn(
              'Failed to update confidence score on swap:',
              confidenceError
            );
            // Don't block the swap if confidence update fails
          }
        }

        // Submit the form if onSubmit handler is provided
        if (onSubmit) {
          // Get current form values and merge with swapped fields
          // We call onSubmit directly instead of handleSubmit because
          // React's state batching means dirtyFields hasn't updated yet
          const currentFormData = formContext.getValues();
          const dataWithSwappedFields = {
            ...currentFormData,
            ...fieldsToUpdate,
            // Pass swapped field keys so onSubmit can bypass dirtyFields check
            _ragSwappedFields: Object.keys(fieldsToUpdate),
          };

          try {
            await onSubmit(dataWithSwappedFields);
          } catch (submitError) {
            console.error('Submit failed:', submitError);
            notifications.error('Failed to save swapped value');
          }
        } else {
          notifications.success('Value swapped to form field');
        }
      } catch (error) {
        console.error('Failed to swap value:', error);
        notifications.error('Failed to swap value');
      }
    },
    [
      formContext,
      fieldName,
      onSubmit,
      onSwapValuesCustom,
      notifications,
      params.submissionId,
      updateScores,
      fieldType,
      fieldMeta,
    ]
  );

  return (
    <ExtractionResultsContent
      extraction={extraction}
      fieldLabel={fieldLabel}
      // A locked submission keeps the extraction view but not the swap, which saves.
      onSwapValues={locked ? undefined : handleSwapValues}
      onCitationClick={onCitationClick}
      isMultiSelect={isMultiSelect}
      currentFieldValue={currentFieldValue}
    />
  );
}

export function FieldExtractionPopover({
  disabled,
  loading,
  isError,
  extraction,
  triggerExtraction,
  fieldLabel,
  fieldName,
  onSubmit,
  onSwapValues,
  isMultiSelect = false,
  currentFieldValue = null,
  fieldType,
  fieldMeta,
}: FieldExtractionPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLElement | null>(null);
  const { state, dispatch } = useDocumentRag();

  const handleOpenChatModal = () => {
    if (state.messageId) {
      dispatch({ type: 'SET_IS_CHAT_OPEN', payload: true });
    }
  };

  const handleReExtractValues = () => {
    triggerExtraction({ force: true });
  };

  const handleCitationClick = (citation: RagSourceReference) => {
    // Set the clicked citation in context
    dispatch({ type: 'SET_CLICKED_CITATION', payload: citation });

    // Set selected document ID
    // Use metadata.source (S3 URI) if available, otherwise use documentId
    const documentIdToSelect =
      citation.metadata?.source && typeof citation.metadata.source === 'string'
        ? citation.metadata.source
        : citation.documentId;

    dispatch({ type: 'SET_SELECTED_DOCUMENT_ID', payload: documentIdToSelect });

    // Open the chat modal
    if (state.messageId) {
      dispatch({ type: 'SET_IS_CHAT_OPEN', payload: true });
    }
  };

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen((prevOpen) => {
      // If the modal is opening, trigger the extraction
      if (prevOpen === false) {
        triggerExtraction();
      }

      return !prevOpen;
    });

    anchorRef.current = event.currentTarget;
  };

  const handleClose = (event: Event | React.SyntheticEvent, reason: string) => {
    // Handle backdrop click and escape key
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      setOpen(false);
    }
  };

  return (
    <>
      <DocumentRagIcon
        onClick={handleToggle}
        loading={loading}
        disabled={disabled}
      />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            overflow: 'visible',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            backgroundColor: 'transparent',
            marginTop: '0.5rem',
          },
        }}
      >
        <ExtractionCardShell
          headerActions={
            <ExtractionCardResultsHeaderActions
              onOpenChatModal={handleOpenChatModal}
              onReExtractValues={handleReExtractValues}
              loading={loading}
            />
          }
        >
          <Show when={loading && !extraction}>
            <ExtractionCardLoadingContent />
          </Show>

          <Show when={Boolean(extraction)}>
            {fieldName ? (
              <FieldExtractionPopoverWithForm
                extraction={extraction}
                fieldName={fieldName}
                fieldLabel={fieldLabel}
                onSubmit={onSubmit}
                onSwapValuesCustom={onSwapValues}
                onCitationClick={handleCitationClick}
                isMultiSelect={isMultiSelect}
                currentFieldValueProp={currentFieldValue}
                fieldType={fieldType}
                fieldMeta={fieldMeta}
              />
            ) : (
              <ExtractionResultsContent
                extraction={extraction}
                onCitationClick={handleCitationClick}
                isMultiSelect={isMultiSelect}
              />
            )}
          </Show>

          <Show when={!loading && !isError && !extraction}>
            <Box sx={{ padding: '1rem' }}>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {fieldLabel}{' '}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
              >
                not found in this Submission's documents
              </Typography>
            </Box>
          </Show>

          <Show when={isError}>
            <Typography>
              An error occurred while extracting the field.
            </Typography>
          </Show>
        </ExtractionCardShell>
      </Popover>
    </>
  );
}
